import React from 'react';
import {
  Skeleton,
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
  Container,
  LinearProgress,
  Grid,
} from '@mui/material';

export const ProgressPageSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container sx={{}}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Skeleton variant="text" width={150} height={32} />
        <Box display="flex" alignItems="center" gap={1}>
          <Skeleton variant="rectangular" width={20} height={20} />
          <Skeleton variant="text" width={60} height={20} />
        </Box>
      </Box>

      {/* Explorer Card - Current Level */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 8,
          background:
            'linear-gradient(135deg, rgba(79, 241, 222, 0.5) 0%, rgba(79, 241, 222, 0.5) 100%)',
          border: 'none',
          p: 2,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box display="flex" alignItems="center" gap={3}>
            {/* Level Icon */}
            <Box
              sx={{
                width: !isMobile ? 60 : 50,
                height: !isMobile ? 60 : 50,
                padding: isMobile ? 2 : 0,
                borderRadius: '50%',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              <Skeleton
                variant="rectangular"
                width={24}
                height={isMobile ? 18 : 24}
                sx={{ bgcolor: '#4FF1DE' }}
              />
            </Box>

            {/* Level Info */}
            <Box flex={1}>
              <Skeleton
                variant="text"
                width={80}
                height={24}
                sx={{ mb: 1, bgcolor: 'rgba(255,255,255,0.7)' }}
              />
              <Skeleton
                variant="text"
                width="90%"
                height={18}
                sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.5)' }}
              />

              {/* Stats */}
              <Box display="flex" gap={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton
                    variant="circular"
                    width={16}
                    height={16}
                    sx={{ bgcolor: 'rgba(239, 68, 68, 0.7)' }}
                  />
                  <Skeleton
                    variant="text"
                    width={70}
                    height={16}
                    sx={{ bgcolor: 'rgba(255,255,255,0.6)' }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton
                    variant="circular"
                    width={16}
                    height={16}
                    sx={{ bgcolor: 'rgba(251, 191, 36, 0.7)' }}
                  />
                  <Skeleton
                    variant="text"
                    width={80}
                    height={16}
                    sx={{ bgcolor: 'rgba(255,255,255,0.6)' }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Your Journey Card */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 8,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton variant="text" width={120} height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={180} height={16} sx={{ mb: 3 }} />

          {/* Journey Progress Icons */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
            {/* Beginner */}
            <Box textAlign="center">
              <Box
                sx={{
                  width: isSmall ? 40 : 50,
                  height: isSmall ? 40 : 50,
                  borderRadius: '50%',
                  bgcolor: '#FFE665',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  mx: 'auto',
                }}
              >
                <Skeleton variant="rectangular" width={16} height={16} sx={{ bgcolor: 'white' }} />
              </Box>
              <Skeleton variant="text" width={60} height={14} sx={{ mx: 'auto', mb: 0.5 }} />
              <Skeleton variant="text" width={50} height={12} sx={{ mx: 'auto' }} />
            </Box>

            {/* Explorer - Active */}
            <Box textAlign="center">
              <Box
                sx={{
                  width: isSmall ? 40 : 50,
                  height: isSmall ? 40 : 50,
                  borderRadius: '50%',
                  bgcolor: '#4FF1DE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  mx: 'auto',
                  boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.3)',
                }}
              >
                <Skeleton variant="rectangular" width={16} height={16} sx={{ bgcolor: 'white' }} />
              </Box>
              <Skeleton variant="text" width={60} height={14} sx={{ mx: 'auto', mb: 0.5 }} />
              <Skeleton variant="text" width={60} height={12} sx={{ mx: 'auto' }} />
            </Box>

            {/* Engager */}
            <Box textAlign="center">
              <Box
                sx={{
                  width: isSmall ? 40 : 50,
                  height: isSmall ? 40 : 50,
                  borderRadius: '50%',
                  bgcolor: '#D4C6F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  mx: 'auto',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={16}
                  height={16}
                  sx={{ bgcolor: '#9ca3af' }}
                />
              </Box>
              <Skeleton variant="text" width={50} height={14} sx={{ mx: 'auto', mb: 0.5 }} />
              <Skeleton variant="text" width={55} height={12} sx={{ mx: 'auto' }} />
            </Box>

            {/* Achiever */}
            <Box textAlign="center">
              <Box
                sx={{
                  width: isSmall ? 40 : 50,
                  height: isSmall ? 40 : 50,
                  borderRadius: '50%',
                  bgcolor: '#BBF484',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  mx: 'auto',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={16}
                  height={16}
                  sx={{ bgcolor: '#9ca3af' }}
                />
              </Box>
              <Skeleton variant="text" width={55} height={14} sx={{ mx: 'auto', mb: 0.5 }} />
              <Skeleton variant="text" width={60} height={12} sx={{ mx: 'auto' }} />
            </Box>

            {/* Champion */}
            <Box textAlign="center">
              <Box
                sx={{
                  width: isSmall ? 40 : 50,
                  height: isSmall ? 40 : 50,
                  borderRadius: '50%',
                  bgcolor: '#FFAF83',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  mx: 'auto',
                }}
              >
                <Skeleton
                  variant="rectangular"
                  width={16}
                  height={16}
                  sx={{ bgcolor: '#9ca3af' }}
                />
              </Box>
              <Skeleton variant="text" width={60} height={14} sx={{ mx: 'auto', mb: 0.5 }} />
              <Skeleton variant="text" width={65} height={12} sx={{ mx: 'auto' }} />
            </Box>
          </Box>

          {/* Progress Bar Section */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Skeleton variant="text" width={140} height={20} />
              <Skeleton variant="text" width={100} height={16} />
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={40}
                sx={{
                  height: 12,
                  borderRadius: 2,
                  bgcolor: '#e5e7eb',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#019df2',
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Skeleton variant="text" width={60} height={16} />
              <Skeleton variant="text" width={70} height={16} />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* All Tiers Section */}
      <Box>
        <Skeleton variant="text" width={80} height={28} sx={{ mb: 3 }} />

        {/* Tier Cards Grid */}
        <Grid container spacing={3}>
          {/* Beginner */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 8,
                border: '1px solid #FFE665',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#FFE665',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
                <Skeleton variant="text" width={60} height={16} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" height={14} />
              </CardContent>
            </Card>
          </Grid>

          {/* Explorer - Highlighted */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 8,
                border: '2px solid #4FF1DE',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                bgcolor: '#ecfdf5',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#4FF1DE',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
                <Skeleton variant="text" width={70} height={16} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="95%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={14} />
              </CardContent>
            </Card>
          </Grid>

          {/* Engager */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 8,
                border: '1px solid #D4C6F9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#D4C6F9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
                <Skeleton variant="text" width={70} height={16} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="75%" height={14} />
              </CardContent>
            </Card>
          </Grid>

          {/* Achiever */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 8,
                border: '1px solid #BBF484',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#BBF484',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                  <Skeleton variant="text" width={70} height={20} />
                </Box>
                <Skeleton variant="text" width={70} height={16} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="95%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="80%" height={14} />
              </CardContent>
            </Card>
          </Grid>

          {/* Champion */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 8,
                border: '1px solid #FFAF83',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#FFAF83',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={16}
                      height={16}
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                  <Skeleton variant="text" width={80} height={20} />
                </Box>
                <Skeleton variant="text" width={70} height={16} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="85%" height={14} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={14} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
